import {
  DistributionClass,
  getDistribtionClass as getDistributionClass,
} from '@upright/common/model/distributions/types';
import { impactCategoryIds } from '@upright/common/model/input/impacts/impact-categories';
import { ModelEntityType } from '@upright/common/model/input/types';
import { ModelResultWithScores } from '@upright/common/model/output/scores/model-entity-data/types';
import { getScoreReaderTotal, ScoreReader } from '@upright/common/model/output/scores/score-reader';
import {
  fromImpactScoreIVKey,
  ImpactScoreIVKey,
  impactScoreKeys,
  NetScoreFormat,
} from '@upright/common/model/output/scores/types';
import { Valence } from '@upright/common/model/types';
import { useTopContributors } from '@upright/ui/api/model/output/scores';
import { TopImpactData } from '@upright/ui/common/upright/core/impact-lenses/net-impact/types';
import { maxBy } from 'lodash';
import { useImpactPercentiles } from './use-impact-percentiles';

export function useTopImpactDatas(
  entityType: ModelEntityType | undefined,
  result: ModelResultWithScores | undefined,
): {
  data: TopImpactData | undefined;
} {
  const dClass = getDistributionClass(entityType);
  const topPositive = useTopImpactData(entityType, result, Valence.POSITIVE, dClass);
  const topNegative = useTopImpactData(entityType, result, Valence.NEGATIVE, dClass);

  const distinctnessData = useDistinctnessData(result, dClass);

  return {
    data: {
      [Valence.POSITIVE]: topPositive,
      [Valence.NEGATIVE]: topNegative,
      distinctnessData,
    },
  };
}

function useTopImpactData(
  entityType: ModelEntityType | undefined,
  result: ModelResultWithScores | undefined,
  valence: Valence.NEGATIVE | Valence.POSITIVE,
  dClass: DistributionClass | undefined,
) {
  const largestImpact = useLargestImpactContributors(entityType, result, valence);
  const mostDistinctImpact = useDistinctImpact(valence, result, dClass);

  return { largestImpact, mostDistinctImpact };
}

function useLargestImpactContributors(
  entityType: ModelEntityType | undefined,
  result: ModelResultWithScores | undefined,
  valence: Valence.NEGATIVE | Valence.POSITIVE,
) {
  const scoreReader = result && new ScoreReader(result.impactScoresByTIVV, undefined, result.revenue);

  const largestCategory = maxBy(impactCategoryIds, k => scoreReader?.readScore(k, undefined, undefined, valence));

  const topContributorsQuery = useTopContributors(entityType, result?.resultId, largestCategory, valence, {
    limit: 1,
  });

  const exampleContributor = topContributorsQuery.data?.contributors.at(0);

  const subContributorsQuery = useTopContributors(
    exampleContributor?.entityType,
    exampleContributor?.variantId ?? exampleContributor?.resultId,
    largestCategory,
    valence,
    { limit: 2 },
  );
  if (!largestCategory) {
    return undefined;
  }

  return {
    impactCode: largestCategory,
    topContributors:
      exampleContributor?.label && exampleContributor.entityId && exampleContributor.entityType
        ? [
            {
              name: exampleContributor.label,
              type: exampleContributor.entityType,
              id: exampleContributor.entityId,
              accessLevel: exampleContributor.accessLevel,
              topContributors:
                subContributorsQuery.data?.contributors
                  .filter(p => p.label && p.entityId && p.entityId)
                  .map(p => ({
                    name: p.label!,
                    type: p.entityType!,
                    id: p.entityId!,
                  })) ?? (subContributorsQuery.isLoading ? ('loading' as const) : undefined),
            },
          ]
        : undefined,
  };
}

function useDistinctImpact(
  valence: Valence.NEGATIVE | Valence.POSITIVE,
  result: ModelResultWithScores | undefined,
  dClass: DistributionClass | undefined,
) {
  const distinctnessData = useDistinctnessData(result, dClass);
  const topCategory = maxBy(
    impactScoreKeys.filter(k => fromImpactScoreIVKey(k).valence === valence),
    k => distinctnessData.get(k)?.percentile,
  );

  if (!topCategory) {
    return undefined;
  }

  return {
    impactCode: fromImpactScoreIVKey(topCategory).impactCode,
    percentile: distinctnessData.get(topCategory)!.percentile!,
  };
}

export function useDistinctnessData(result: ModelResultWithScores | undefined, dClass: DistributionClass | undefined) {
  const impactScores = resultToImpactScores(result);
  const distinctnessData = useImpactPercentiles(impactScores, dClass);
  return distinctnessData;
}

function resultToImpactScores(
  result: ModelResultWithScores | undefined,
): Map<ImpactScoreIVKey | 'nir' | 'nis', number | undefined> {
  const scoreReader = result && new ScoreReader(result.impactScoresByTIVV, undefined, result.revenue);
  return new Map([
    ['nir', scoreReader ? getScoreReaderTotal(scoreReader, NetScoreFormat.NET_RATIO) : undefined],
    ['nis', scoreReader ? getScoreReaderTotal(scoreReader, NetScoreFormat.NET_SUM) : undefined],
    ...impactScoreKeys.map(key => {
      const { valence, impactCode } = fromImpactScoreIVKey(key);
      const score = scoreReader?.readScore(impactCode, undefined, undefined, valence);
      return [key, score] as const;
    }),
  ]);
}
